import { useCallback } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { HIDDEN, MT_SHELL_LAYOUT, ROUTES_NAMESPACE } from '~/config/app.constants';
import routes from '~/components/Routes/config/routesConfig';
import type { TMicroAppDetails } from '~/components/Routes/types';
import { StyledShellLayout } from '../styles';

const noLayoutRoutes =
  routes.protectedRoutes.filter((route: TMicroAppDetails) => route.layout === false) || [];

const matchesNoLayoutPath = (pathname: string) =>
  noLayoutRoutes.some(({ path }) => {
    if (!path) return false;

    const namespacedPaths = ROUTES_NAMESPACE.map(ns => {
      const replaced = ns.replace('*', path);
      return replaced.startsWith('/') ? replaced : `/${replaced}`;
    });

    return namespacedPaths.some(pattern => !!matchPath(pattern, pathname));
  });

const isMtShellLayoutHidden = (search: string) => {
  const params = new URLSearchParams(search);
  if (!params.has(MT_SHELL_LAYOUT)) return false;
  const value = params.get(MT_SHELL_LAYOUT);
  return value === HIDDEN;
};

type WithConditionalLayoutProps = {
  children: React.ReactNode;
};

export const withConditionalLayout = <P extends object>(
  LayoutComponent: React.ComponentType<P>
) => (props: P & WithConditionalLayoutProps) => {
  const location = useLocation();
  const shouldHideLayout = useCallback(
    () => matchesNoLayoutPath(location.pathname) || isMtShellLayoutHidden(location.search),
    [location.search, location.pathname]
  );

  const { children, ...layoutProps } = props;

  if (shouldHideLayout()) {
    return <StyledShellLayout>{children}</StyledShellLayout>;
  }

  return <LayoutComponent {...(layoutProps as P)}>{children}</LayoutComponent>;
};
